import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Badge, Card, CardHeader, Detail, EmptyState, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { KpiCard, PeriodLine, useNumber } from '../components/kit';
import { BackLink, Loaded, Note, PriorityBadge, StatusBadge, TextLink, sourceHref, useAmount, useCompany, useDates, usePercent } from '../components/b-shared';
import { ProjectsTable, type ClientRow, type ProjectRow, type WithPeriod } from '../components/b-projects';

/**
 * One client (CR-008 §9.3): their projects, tickets and service health — SLA compliance and reopens, since
 * there is no satisfaction survey to read — what they paid in the period and still owe, their contracts and
 * their open invoices.
 */

interface ClientDetail extends ClientRow, WithPeriod {
  projectRows: ProjectRow[];
  recentTickets: Array<{ id: string; number: string; subject: string; status: string; priority: string; createdAt: string; resolvedAt: string | null }>;
  openInvoices: Array<{
    id: string;
    number: string;
    issueDate: string;
    dueDate: string;
    currency: string;
    totalAmount: number;
    remaining: number;
    status: string;
    overdue: boolean;
  }>;
  satisfaction: { available: boolean; reason: string };
}

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const { params, query } = usePeriod();
  const detail = useQuery({
    queryKey: ['executive', 'client', id, params],
    queryFn: () => api.get<ClientDetail>(`/executive/clients/${id}`, { ...params }),
    enabled: !!id,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === id ? previous : undefined),
  });
  const back = new URLSearchParams(query);
  back.set('tab', 'clients');
  return (
    <>
      <BackLink to={`/projects?${back.toString()}`}>{t('clients.back')}</BackLink>
      <Loaded query={detail}>
        {(data) =>
          data.client ? (
            <ClientView data={data} />
          ) : (
            <Card>
              <EmptyState title={t('common.noResults')} hint={t('clients.beyondList')} />
            </Card>
          )
        }
      </Loaded>
    </>
  );
}

function ClientView({ data }: { data: ClientDetail }) {
  const { t, localized } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  const pct = usePercent();
  const amount = useAmount();
  const dates = useDates();
  const { baseCurrency } = useCompany();
  const tz = data.period.timezone;
  const currency = data.money.currency || baseCurrency;
  return (
    <>
      <PageHeader
        title={
          <span>
            {localized(data.client)}
            <span dir="ltr" className="ms-2 font-mono text-body text-[var(--text-tertiary)]">
              {data.client.code}
            </span>
          </span>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusBadge status={data.client.status} />
            <span dir="ltr" className="font-mono text-caption">
              {data.client.countryCode} · {data.client.defaultCurrency}
            </span>
          </span>
        }
      />
      <div className="space-y-5">
        <PeriodLine period={data.period} />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KpiCard label={t('clients.col.projects')} value={num(data.projects.total)} hint={t('clients.delivering', { n: num(data.projects.delivering) })} />
          <KpiCard label={t('clients.col.openTickets')} value={num(data.tickets.open)} />
          <KpiCard label={t('clients.col.resolved')} value={num(data.tickets.resolvedInPeriod)} />
          <KpiCard label={t('clients.col.reopened')} value={num(data.tickets.reopenedInPeriod)} tone={data.tickets.reopenedInPeriod > 0 ? 'warning' : 'neutral'} />
          <KpiCard label={t('clients.col.overdueInvoices')} value={num(data.money.overdueInvoices)} tone={data.money.overdueInvoices > 0 ? 'error' : 'neutral'} />
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          <Card>
            <CardHeader title={t('clients.service.title')} subtitle={t('clients.service.subtitle')} />
            <dl>
              <Detail label={t('clients.col.assignmentSla')}>{pct(data.service.assignmentSlaPercent)}</Detail>
              <Detail label={t('clients.col.viewSla')}>{pct(data.service.viewSlaPercent)}</Detail>
              <Detail label={t('clients.service.decided')}>{num(data.service.decided)}</Detail>
              <Detail label={t('clients.service.satisfaction')}>
                <span className="text-muted">{t('state.unavailable')}</span>
              </Detail>
            </dl>
            <Note className="mt-3">{t('clients.service.noSurvey')}</Note>
          </Card>
          <Card>
            <CardHeader title={t('clients.money.title')} subtitle={t('clients.moneyNote')} />
            <dl>
              <Detail label={t('clients.col.collected')}>
                <span className="tabular">{amount(data.money.collectedInPeriod, baseCurrency)}</span>
              </Detail>
              <Detail label={t('projects.col.invoiced')}>
                {data.money.invoiced === null ? <span className="text-warning-fg dark:text-warning-dark">{t('common.unknown')}</span> : <span className="tabular">{amount(data.money.invoiced, currency)}</span>}
              </Detail>
              <Detail label={t('clients.col.outstanding')}>
                {data.money.outstanding === null ? (
                  <span className="text-warning-fg dark:text-warning-dark">{t('common.unknown')}</span>
                ) : (
                  <span className="tabular">{amount(data.money.outstanding, currency)}</span>
                )}
              </Detail>
            </dl>
            {data.money.unconverted.length > 0 && <Note className="mt-3" tone="warning">{t('projects.noRate', { currencies: data.money.unconverted.join(', ') })}</Note>}
          </Card>
          <Card>
            <CardHeader title={t('clients.col.contracts')} />
            <dl>
              <Detail label={t('clients.contracts.covering')}>{num(data.contracts.covering)}</Detail>
              <Detail label={t('clients.contracts.endingSoon', { days: num(data.contracts.endingSoonDays) })}>
                {data.contracts.endingSoon > 0 ? <Badge tone="warning">{num(data.contracts.endingSoon)}</Badge> : num(0)}
              </Detail>
            </dl>
          </Card>
        </div>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('clients.detail.projects')} />
          </div>
          {data.projectRows.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('clients.detail.noProjects')}</p>
          ) : (
            <ProjectsTable rows={data.projectRows} query={query} showClient={false} />
          )}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('clients.detail.tickets')} subtitle={t('clients.detail.ticketsHint')} />
          </div>
          {data.recentTickets.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('clients.detail.noTickets')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.ticket')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('projects.detail.opened')}</Th>
                  <Th>{t('evidence.item.resolved')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.recentTickets.map((ticket) => (
                  <Tr key={ticket.id}>
                    <Td className="min-w-[14rem]">
                      <TextLink to={sourceHref('TICKET', ticket.id, query) ?? '#'}>
                        <span dir="ltr" className="me-1.5 font-mono text-caption text-[var(--text-tertiary)]">
                          {ticket.number}
                        </span>
                        {ticket.subject}
                      </TextLink>
                    </Td>
                    <Td>
                      <StatusBadge status={ticket.status} />
                    </Td>
                    <Td>
                      <PriorityBadge priority={ticket.priority} />
                    </Td>
                    <Td className="whitespace-nowrap">{dates.at(ticket.createdAt, tz)}</Td>
                    <Td className="whitespace-nowrap">{dates.at(ticket.resolvedAt, tz)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('clients.detail.invoices')} subtitle={t('clients.detail.invoicesHint')} />
          </div>
          {data.openInvoices.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('clients.detail.noInvoices')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('clients.detail.invoice')}</Th>
                  <Th>{t('clients.detail.issued')}</Th>
                  <Th>{t('clients.detail.due')}</Th>
                  <Th align="end">{t('clients.detail.total')}</Th>
                  <Th align="end">{t('clients.detail.remaining')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.openInvoices.map((inv) => (
                  <Tr key={inv.id}>
                    <Td>
                      <span dir="ltr" className="font-mono text-caption">
                        {inv.number}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">{dates.day(inv.issueDate)}</Td>
                    <Td className="whitespace-nowrap">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {dates.day(inv.dueDate)}
                        {inv.overdue && <Badge tone="error">{t('clients.detail.overdue')}</Badge>}
                      </span>
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(inv.totalAmount, inv.currency)}
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(inv.remaining, inv.currency)}
                    </Td>
                    <Td>
                      <StatusBadge status={inv.status} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
